import { secFrom100nsec } from './Utils';
import { GetNumericSpeakerIdFromSpeakerId } from './Utils';
import { ITranscription, ITranscriptionSegment } from './interfaces/ITranscriptionManager';

const frontDoorEndpoint = 'https://pptservicescast.edog.officeapps.live.com';

enum FrontDoorBatchProcessingStatus {
    Pending = 1,
    Completed = 2,
    Error = 3
}

export class FrontDoorFileUpload {
    private abortController: AbortController;

    public async UploadFileAndGetTranscript(file: File): Promise<ITranscription> {
        this.abortController = new AbortController();
        const conversationId = await this.AnalyzeUploadedAudio(file);

        // The raw transcript returned by CTS has the following fields:
        // userId,resultId, reason, text, durationInTicks, offsetInTicks,,nBest
        const rawTranscript = await this.GetBatchConversationTranscription(conversationId);

        const transcript: ITranscription = {
            transcriptSegments: [],
        };
    
        let maxSpeakerId = null;
    
        for(let i = 0; i < rawTranscript.length; i++) {
            const startTimestamp = secFrom100nsec(rawTranscript[i].offsetInTicks);
            const endTimestamp = startTimestamp + secFrom100nsec(rawTranscript[i].durationInTicks);
            const speakerId = GetNumericSpeakerIdFromSpeakerId(rawTranscript[i].userId);
    
            if (speakerId != null) {
                maxSpeakerId = maxSpeakerId == null ? speakerId : Math.max(speakerId, maxSpeakerId);
            }

            transcript.transcriptSegments.push({
                text: rawTranscript[i].text,
                language: "en-US",
                start: startTimestamp,
                end: endTimestamp, 
                speakerId
            });
        }
    
        transcript.speakerNames = new Array(maxSpeakerId != null ? maxSpeakerId + 1 : 0);
    
        // We need to sort the segments because speedy CTS may return them out of order due to paralellization on their side.
        transcript.transcriptSegments.sort((segment1: ITranscriptionSegment, segment2: ITranscriptionSegment) => {
            return segment1.start - segment2.start;
        });

        return transcript;
    }

    public Cancel() {
        if (this.abortController != null)
            this.abortController.abort();
    }

    private async AnalyzeUploadedAudio(file: File): Promise<string> {
        const formData = new FormData();
        formData.append('userSessionId', 'Test_UserId');
        formData.append('app', 'OfficeSuite');
        formData.append('sourceLanguage', 'en-US');
        formData.append('scenarioName', 'WordTranscription');
        formData.append('client', 'web');
        formData.append('speechMode', 'ConversationTranscriptionAsync');
        formData.append('file', file);
    
        const response = await fetch(frontDoorEndpoint + "/SpeechHandler.ashx?action=AnalyzeUploadedAudio", { method: "POST", body: formData, signal: this.abortController.signal });
    
        if (response.status != 200)
            throw new Error("Analyze uploaded audio returned non-successful status code " + response.status);
    
        const jsonResponse = await response.json();
        
        if (jsonResponse.conversationId == null)
            throw new Error("Didn't get conversationId");
    
        return jsonResponse.conversationId;
    }

    private async GetBatchConversationTranscription(conversationId: string): Promise<any> {
        const response = await fetch(frontDoorEndpoint + "/SpeechHandler.ashx?action=GetBatchConversationTranscription&id=" + conversationId, { method: "GET", signal: this.abortController.signal });
        const jsonResponse = await response.json();
    
        if (jsonResponse.processingStatus != FrontDoorBatchProcessingStatus.Completed)
            throw new Error("GetBatchConversationTranscription returned invalid processing status");

        return jsonResponse.transcriptions;
    }
}